/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import ActionsMenu from '../../components/shared/ActionsMenu'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import { Role } from '../../constants/user'
import { SystemFolderType } from '../../constants/fsi'
import ShareReportModal from './ShareReportModal'
import FavoriteNameModal from './FavoriteNameModal'
import MakeObsoleteModal from './MakeObsoleteModal'
import MakeUnobsoleteModal from './MakeUnobsoleteModal'
import FolderAndDocumentDetailsModal from '~/components/modals/FolderAndDocumentDetailsModal'
import RenameFolderAndDocumentModal from './RenameModal'
import { removeFileFromFavorite, getFileDownloadUrl } from '../../services/api/files'

// Loading placeholder constant
const LOADING_MENU_PLACEHOLDER = {
  value: 'LOADING...',
  disabled: true
}

interface ReportsTableMenuProps {
  fsi: any
  options?: {
    selectedWholeResult?: boolean
    selectedIds?: string[]
    selectedItems?: any[]
    filter?: any
    deselectAll?: () => void
  }
}

const ReportsTableMenu: React.FC<ReportsTableMenuProps> = ({ fsi, options = {} }) => {
  const context = useServiceContext()
  const { showModal } = useModal()
  const { api, service } = context
  const [menuOptions, setMenuOptions] = useState<any[]>([LOADING_MENU_PLACEHOLDER])
  const [isOpen, setIsOpen] = useState(false)

  // Helper function to download multiple files as ZIP
  const downloadZipOfPDFFiles = (selectedIds: string[], showLoader: boolean, downloadOptions: any) => {
    // Access Angular service via window (still needed for multi-file ZIP download)
    const angular = (window as any).angular
    if (angular) {
      const injector = angular.element(document.body).injector()
      const ValidateFile = injector.get('ValidateFile')
      return ValidateFile.downloadZipOfPDFFiles(selectedIds, showLoader, downloadOptions)
    }
  }

  const isWholeResultSelection = options.selectedWholeResult
  const isMultiSelection = options.selectedIds && options.selectedIds.length > 0
  const isMultiButOnlyOneSelection =
    isMultiSelection && options.selectedIds!.length === 1 && options.selectedIds![0] === fsi.id
  const isFsiIncludedInMultiSelection = isMultiSelection && options.selectedIds!.includes(fsi.id)
  // not pretty much sure why they wanted ETMF_SITE_USER and ETMF_STUDY_USER to have these options (these roles don't have access to this section)
  const isUserRole = [Role.USER, Role.EXTERNAL, Role.ETMF_SITE_USER, Role.ETMF_STUDY_USER].includes(
    service.auth.getRole() as Role
  )

  const configureAndSetMenuOptionsForMultiSelection = () => {
    const menu: any[] = []

    // MULTI-SELECTED FILES
    if (isWholeResultSelection || (isMultiSelection && !isMultiButOnlyOneSelection)) {
      menu.push({
        value: 'BROWSER_ACTION_DOWNLOAD',
        action: () => {
          downloadZipOfPDFFiles(options.selectedIds!, true, {
            selectedWholeResult: isWholeResultSelection,
            filter: options.filter,
            exportedReports: true
          })
        }
      })

      if (isWholeResultSelection && !isUserRole) {
        menu.push({
          value: 'BROWSER_ACTION_MAKE_OBSOLETE',
          action: () => {
            showModal(
              <MakeObsoleteModal
                fileDetails={null}
                options={{
                  selectedIds: options.selectedIds,
                  selectedWholeResult: isWholeResultSelection,
                  filter: options.filter,
                  exportedReports: true
                }}
              />
            )
          }
        })
        menu.push({
          value: 'BROWSER_ACTION_MAKE_UNOBSOLETE',
          action: () => {
            showModal(
              <MakeUnobsoleteModal
                fileDetails={null}
                options={{
                  selectedIds: options.selectedIds,
                  selectedWholeResult: isWholeResultSelection,
                  filter: options.filter,
                  exportedReports: true
                }}
              />
            )
          }
        })
      } else {
        const allFilesAre = options.selectedItems!.reduce(
          (allAre, f) => {
            if (f.obsolete) {
              allAre.effective = false
            } else {
              allAre.obsolete = false
            }
            return allAre
          },
          {
            obsolete: true,
            effective: true
          }
        )

        if (allFilesAre.effective && !isUserRole) {
          menu.push({
            value: 'BROWSER_ACTION_MAKE_OBSOLETE',
            action: () => {
              showModal(
                <MakeObsoleteModal
                  fileDetails={null}
                  options={{
                    selectedIds: options.selectedIds
                  }}
                />
              )
            }
          })
        } else if (allFilesAre.obsolete && !isUserRole) {
          menu.push({
            value: 'BROWSER_ACTION_MAKE_UNOBSOLETE',
            action: () => {
              showModal(
                <MakeUnobsoleteModal
                  fileDetails={null}
                  options={{
                    selectedIds: options.selectedIds
                  }}
                />
              )
            }
          })
        }
      }
    }

    setMenuOptions(menu)
  }

  const configureAndSetMenuOptions = (file: any) => {
    const menu: any[] = []

    menu.push({
      value: 'BROWSER_ACTION_VIEW_DETAILS',
      action: () => {
        showModal(<FolderAndDocumentDetailsModal fileDetails={file} versionInfo={{}} isReport={true} />)
      }
    })

    if (file.actionFlags.canDownloadSource) {
      menu.push({
        value: 'DOWNLOAD',
        action: async () => {
          try {
            const data = await getFileDownloadUrl(Number(file.id))
            if (data.url) {
              window.location.href = data.url
            }
          } catch (error) {
            console.error('Error downloading report:', error)
          }
        }
      })
    }

    // share
    if (file.actionFlags.canManageAcl) {
      menu.push({
        value: 'BROWSER_ACTION_SHARE_REPORT',
        action: () => {
          showModal(<ShareReportModal file={file} onClose={() => {}} />)
        }
      })
    }

    // rename
    if (file.actionFlags.canRename) {
      menu.push({
        value: 'BROWSER_ACTION_RENAME',
        action: () => {
          showModal(<RenameFolderAndDocumentModal fileDetails={file} />)
        }
      })
    }

    // make obsolete
    if (file.obsolete && file.actionFlags.canUnobsolete) {
      menu.push({
        value: 'BROWSER_ACTION_MAKE_UNOBSOLETE',
        action: () => {
          showModal(<MakeUnobsoleteModal fileDetails={file} />)
        }
      })
    } else if (file.actionFlags.canObsolete) {
      menu.push({
        value: 'BROWSER_ACTION_MAKE_OBSOLETE',
        action: () => {
          showModal(<MakeObsoleteModal fileDetails={file} />)
        }
      })
    }

    let showFavMenu = true
    if (file.systemFolderType === SystemFolderType.WORKFLOWS || file.obsolete || file.id === -1) {
      showFavMenu = false
    }

    if (showFavMenu) {
      if (file.isFavorite) {
        menu.push({
          value: 'BROWSER_ACTION_REMOVE_FROM_FAV',
          action: async () => {
            await removeFileFromFavorite(file.id)
            window.dispatchEvent(new CustomEvent('updateFiles'))
          }
        })
      } else {
        menu.push({
          value: 'BROWSER_ACTION_ADD_TO_FAV',
          action: () => {
            showModal(<FavoriteNameModal fileDetails={file} onClose={() => {}} />)
          }
        })
      }
    }

    setMenuOptions(menu)
  }

  useEffect(() => {
    if (!fsi || !fsi.id) {
      throw new Error('Missing file or fileId in action menu')
    }

    if (isOpen) {
      // multi selection
      if (isMultiSelection && !isFsiIncludedInMultiSelection) {
        // if the action menu was opened for file which is not in the current multiselection, deselect all
        if (typeof options.deselectAll === 'function') {
          options.deselectAll()
        }
      } else if (
        isWholeResultSelection ||
        (isMultiSelection && !isMultiButOnlyOneSelection && isFsiIncludedInMultiSelection)
      ) {
        configureAndSetMenuOptionsForMultiSelection()
        return
      }

      // SINGLE FSI MENU
      if (fsi.actionFlags) {
        configureAndSetMenuOptions(fsi)
        return
      }

      const promises = [api.files.getDetails(fsi.id), api.files.getFsiPath(fsi.id, true)]

      Promise.all(promises).then((data) => {
        // Note: Legacy used .plain() but TypeScript API already returns plain objects
        configureAndSetMenuOptions({
          ...data[0],
          path: data[1]
        })
      })
    }
  }, [fsi, isOpen])

  return (
    <>
      <ActionsMenu
        menuId={`action-menu-browser-${fsi.id}`}
        onToggle={(open: boolean) => {
          setIsOpen(open)
        }}
        items={menuOptions}
      />
    </>
  )
}

export default ReportsTableMenu
